import React from "react";
import { useTranslation } from "react-i18next";

/**
 * Spinner — canonical loading indicator for the internal app shell + admin pages.
 *
 * Renders a thin animated ring. The default (`tone="brand"`) is the brand
 * orange-on-cream ring used as the project's standard loading affordance —
 * `animate-spin rounded-full border-2 border-brand-line border-t-brand-orange`.
 *
 * Use this component for ANY new loading affordance. Prefer it over hand-rolled
 * `animate-spin border-...` divs so colors stay consistent across the app.
 *
 * Variants:
 *   - `brand`   (default): orange ring on light gray base — for page/section loaders
 *   - `onDark`           : white ring on white/30 base — for use inside a colored
 *                          button, dark hero, or solid brand-orange background
 *   - `inherit`          : ring inherits `currentColor` — for use inside a button
 *                          where the ring should match the button text color
 *
 * Sizes (h x w in Tailwind units):
 *   - `xs` 3   - `sm` 4   - `md` 5   - `lg` 8   - `xl` 12
 *
 * NOT for use in:
 *   - marketing/landing pages (have their own slate/dark visual identity)
 *   - the embedded chat widget (separate visual identity)
 *   - workflow canvas nodes (separate visual identity)
 */

export type SpinnerSize = "xs" | "sm" | "md" | "lg" | "xl";
export type SpinnerTone = "brand" | "onDark" | "inherit";

interface SpinnerProps {
    size?: SpinnerSize;
    tone?: SpinnerTone;
    /** Wrap the spinner in a centered flex container (block-level). */
    center?: boolean;
    /** Optional label rendered below the spinner. Implies `center`. */
    label?: React.ReactNode;
    className?: string;
    /** Pass-through aria-label. Defaults to the localised "Loading". */
    "aria-label"?: string;
}

const SIZE_CLASSES: Record<SpinnerSize, string> = {
    xs: "h-3 w-3 border-2",
    sm: "h-4 w-4 border-2",
    md: "h-5 w-5 border-2",
    lg: "h-8 w-8 border-2",
    xl: "h-12 w-12 border-2",
};

const TONE_CLASSES: Record<SpinnerTone, string> = {
    brand: "border-brand-line border-t-brand-orange",
    onDark: "border-white/30 border-t-white",
    inherit: "border-current border-t-transparent",
};

export const Spinner: React.FC<SpinnerProps> = ({
    size = "lg",
    tone = "brand",
    center = false,
    label,
    className = "",
    "aria-label": ariaLabel,
}) => {
    const { t } = useTranslation("common");
    const ringCls = ["animate-spin rounded-full", SIZE_CLASSES[size], TONE_CLASSES[tone], className].filter(Boolean).join(" ");

    const ring = <div className={ringCls} role="status" aria-label={ariaLabel ?? t("state.loadingLabel")} />;

    if (label) {
        return (
            <div className="flex flex-col items-center justify-center gap-2">
                {ring}
                <p className="text-sm text-brand-ink/60">{label}</p>
            </div>
        );
    }

    if (center) {
        return <div className="flex items-center justify-center">{ring}</div>;
    }

    return ring;
};

export default Spinner;
